import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { publicFormApi } from '../api/publicForms';
import type { Draft, DraftResponse } from '../types/publicForm.types';
import { useCallback, useEffect, useRef } from 'react';

const DRAFT_STORAGE_KEY = (formId: string) => `form_draft_${formId}`;
const AUTOSAVE_DELAY = 2000; // 2 seconds

export const useFormDraft = (formId: string) => {
  const queryClient = useQueryClient();
  const saveTimeoutRef = useRef<NodeJS.Timeout>();
  const pendingUpdatesRef = useRef<Record<string, any>>({});

  // Get or create draft
  const { data: draft, isLoading: isDraftLoading } = useQuery({
    queryKey: ['formDraft', formId],
    queryFn: async () => {
      // Don't make API calls if formId is empty
      if (!formId) {
        return null;
      }
      
      // Check localStorage for existing auth token
      const storedAuthToken = localStorage.getItem(DRAFT_STORAGE_KEY(formId));
      
      if (storedAuthToken) {
        try {
          // Try to retrieve existing draft
          const existingDraft = await publicFormApi.getDraft(formId, storedAuthToken);
          return existingDraft;
        } catch (error) {
          // Draft not found or expired, create new one
          console.warn('Could not retrieve draft, creating new one');
          localStorage.removeItem(DRAFT_STORAGE_KEY(formId));
        }
      }
      
      // Create new draft
      const newDraft = await publicFormApi.createDraft(formId);
      localStorage.setItem(DRAFT_STORAGE_KEY(formId), newDraft.authToken);
      return newDraft;
    },
    enabled: !!formId, // Only run query when formId is available
    staleTime: Infinity, // Draft doesn't go stale
    retry: 3,
  });

  // Update draft mutation
  const updateDraftMutation = useMutation({
    mutationFn: async (updates: { submission?: any; responses?: any }) => {
      if (!draft?.authToken) throw new Error('No draft available');
      return publicFormApi.updateDraft(formId, draft.authToken, updates);
    },
    onSuccess: (updatedDraft) => {
      queryClient.setQueryData(['formDraft', formId], updatedDraft);
    },
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });

  // Debounced save function
  const saveChanges = useCallback((updates: { submission?: any; responses?: any }) => {
    if (!draft?.authToken) return;

    // Merge with pending updates
    pendingUpdatesRef.current = {
      ...pendingUpdatesRef.current,
      ...updates,
      responses: {
        ...pendingUpdatesRef.current.responses,
        ...updates.responses,
      },
    };

    // Clear existing timeout
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    // Set new timeout
    saveTimeoutRef.current = setTimeout(() => {
      const updatesToSave = { ...pendingUpdatesRef.current };
      pendingUpdatesRef.current = {};
      
      updateDraftMutation.mutate(updatesToSave);
    }, AUTOSAVE_DELAY);
  }, [draft?.authToken, updateDraftMutation]);

  // Save immediately (bypasses debouncing)
  const saveNow = useCallback(async () => {
    if (!draft?.authToken || Object.keys(pendingUpdatesRef.current).length === 0) {
      return;
    }

    // Clear timeout
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    // Save pending updates
    const updatesToSave = { ...pendingUpdatesRef.current };
    pendingUpdatesRef.current = {};
    
    await updateDraftMutation.mutateAsync(updatesToSave);
  }, [draft?.authToken, updateDraftMutation]);

  // Clear draft from storage
  const clearDraft = useCallback(() => {
    localStorage.removeItem(DRAFT_STORAGE_KEY(formId));
    queryClient.removeQueries({ queryKey: ['formDraft', formId] });
  }, [formId, queryClient]);

  // Save on unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
        // Save synchronously on unmount if possible
        if (pendingUpdatesRef.current && Object.keys(pendingUpdatesRef.current).length > 0) {
          navigator.sendBeacon(
            `/api/portal/forms/${formId}/drafts/${draft?.authToken}`,
            JSON.stringify(pendingUpdatesRef.current)
          );
        }
      }
    };
  }, [formId, draft?.authToken]);

  return {
    draft,
    isDraftLoading,
    isSaving: updateDraftMutation.isPending,
    lastSaved: updateDraftMutation.isSuccess ? new Date() : null,
    saveError: updateDraftMutation.error,
    saveChanges,
    saveNow,
    clearDraft,
  };
};